import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

function formatBR(dateIso) {
  if (!dateIso) return "";
  const [y, m, d] = dateIso.split("-");
  return `${d}/${m}/${y}`;
}

function GameLine({ g }) {
  return (
    <div className="rounded-lg border border-border/60 p-3 bg-muted/30 text-sm">
      <div className="font-black text-base">{g.competition}</div>
      <div className="text-muted-foreground mt-1">
        <div><b>Data:</b> {formatBR(g.date)}</div>
        <div><b>Horário:</b> {g.time}</div>
        <div><b>Local:</b> {g.location}</div>
        <div><b>Categoria:</b> {g.category} • {g.gender === "F" ? "Feminino" : "Masculino"}</div>
      </div>
    </div>
  );
}

/**
 * Carrega alertas pendentes do aluno (convocado/cortado/inadimplente)
 * e mostra pop-ups ao abrir o app. Marca como visto ao fechar.
 */
export default function AlertManager() {
  const [state, setState] = useState({ loaded: false, calls: [], decalls: [], overdue: false });
  const [showCalls, setShowCalls] = useState(false);
  const [showDecalls, setShowDecalls] = useState(false);
  const [showOverdue, setShowOverdue] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/alerts");
        setState({ loaded: true, ...data });
        if (data.calls?.length) setShowCalls(true);
        if (data.decalls?.length) setShowDecalls(true);
        if (data.overdue) setShowOverdue(true);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const markSeen = async () => {
    try { await api.post("/me/alerts/seen"); } catch (e) {}
  };

  const closeCalls = async () => {
    setShowCalls(false);
    await markSeen();
    nav("/aluno/calendario");
  };

  const closeDecalls = async () => {
    setShowDecalls(false);
    await markSeen();
  };

  const callsTitle = state.calls[0]?.title || "CONVOCADO!";
  const decallsTitle = state.decalls[0]?.title || "DESCONVOCADO!";

  return (
    <>
      <Dialog open={showCalls} onOpenChange={setShowCalls}>
        <DialogContent className="max-w-md" data-testid="alert-call-dialog">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black vv-gradient-text">{callsTitle}</DialogTitle>
            <DialogDescription className="text-base">
              Você foi convocado{callsTitle.endsWith("A!") ? "a" : ""} para {state.calls.length > 1 ? `${state.calls.length} jogos` : "o jogo"} abaixo:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {state.calls.map((c) => <GameLine key={c.alert_id} g={c.game} />)}
          </div>
          <Button onClick={closeCalls} data-testid="close-call-alert" className="w-full bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider">
            OK, entendi
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showDecalls} onOpenChange={setShowDecalls}>
        <DialogContent className="max-w-md" data-testid="alert-decall-dialog">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black text-[hsl(0_80%_70%)]">{decallsTitle}</DialogTitle>
            <DialogDescription className="text-base">
              Você foi cortado{decallsTitle.endsWith("A!") ? "a" : ""} de {state.decalls.length > 1 ? `${state.decalls.length} jogos` : "um jogo"}:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {state.decalls.map((c) => <GameLine key={c.alert_id} g={c.game} />)}
          </div>
          <Button onClick={closeDecalls} data-testid="close-decall-alert" className="w-full bg-[hsl(var(--vv-yellow))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider">
            OK, entendi
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showOverdue} onOpenChange={setShowOverdue}>
        <DialogContent className="max-w-md" data-testid="alert-overdue-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[hsl(0_80%_70%)]">Mensalidade em atraso</DialogTitle>
            <DialogDescription className="text-base">
              Sua mensalidade está em atraso, regularize sua situação.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => setShowOverdue(false)} data-testid="close-overdue-alert" className="w-full bg-[hsl(0_70%_60%)] text-white font-bold uppercase tracking-wider">
            OK, entendi
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
